import React, { useMemo, useRef, useState } from 'react'
import { MoreHorizontal, Copy, Edit2, Trash2, MessageSquare, ChevronDown, ChevronRight } from 'lucide-react'
import { Menu, MenuItem, MenuItems, MenuButton } from '@headlessui/react'
import { toast } from 'react-hot-toast'
import { CrmComment, CrmCommentHooks } from 'types/crm/comments'
import { ContactOwner } from 'types/crm/contacts'
import { getInitials } from 'utils/getInitials'
import { getExactFormatDate } from 'utils/helpers'
import { useAppSelector } from 'store'
import { selectAuthUser } from 'store/slices/auth/auth.selector'
import TipTapEditor from 'components/common/RichTextEditor/RichTextEditor'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
import CrmReplyItem from './CrmReplyItem'
import CrmReplyComposer from './CrmReplyComposer'

interface CrmCommentThreadProps {
    comment: CrmComment
    parentUuid: string
    entityUuid: string
    commentHooks: CrmCommentHooks
    mentionUsers?: ContactOwner[]
    onDeleted?: () => void
    onEdited?: () => void
}

const sortByCreatedAt = (list: CrmComment[]) =>
    [...list].sort(
        (a, b) => new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime(),
    )

const dedupeByUuid = (list: CrmComment[]) => {
    const map = new Map(list.map((c) => [c.commentUuid, c]))
    return Array.from(map.values())
}

const CrmCommentThread: React.FC<CrmCommentThreadProps> = ({
    comment,
    parentUuid,
    entityUuid,
    commentHooks,
    mentionUsers = [],
    onDeleted,
    onEdited,
}) => {
    const hasReplies = (comment.replyCount ?? 0) > 0

    const currentUser = useAppSelector(selectAuthUser)
    const currentUserId = currentUser?.appUserUuid

    const { mutate: submitComment, isPending: isSubmitting } = commentHooks.useAddComment()
    const { mutate: deleteComment, isPending: isDeletePending } = commentHooks.useDeleteComment()
    const { mutate: updateComment, isPending: isUpdating } = commentHooks.useUpdateComment()

    const [isExpanded, setIsExpanded] = useState(false)

    // Level-1 pagination (root's own direct children) — this is the only
    // level the "Show more replies" button controls. Any deeper nesting is
    // fetched eagerly and silently flattened in, so the user never sees a
    // second "show more" for a sub-thread — matches the flatten requirement.
    const [level1Page, setLevel1Page] = useState(0)
    const [level1TotalPages, setLevel1TotalPages] = useState(1)
    const [allReplies, setAllReplies] = useState<CrmComment[]>([])
    const [isLoadingReplies, setIsLoadingReplies] = useState(false)
    const fetchedNestedFor = useRef<Set<string>>(new Set())

    // Root comment's own inline edit
    const [isEditingRoot, setIsEditingRoot] = useState(false)
    const [editText, setEditText] = useState(comment.commentText)

    const [replyTarget, setReplyTarget] = useState<CrmComment | null>(null)
    const [replyText, setReplyText] = useState('')

    const [editingReplyUuid, setEditingReplyUuid] = useState<string | null>(null)
    const [editingReplyText, setEditingReplyText] = useState('')

    const [deleteTarget, setDeleteTarget] = useState<CrmComment | null>(null)

    const [highlightedUuid, setHighlightedUuid] = useState<string | null>(null)

    const handleJumpToParent = (parentCommentUuid: string) => {
        const el = document.getElementById(`comment-${parentCommentUuid}`)
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setHighlightedUuid(parentCommentUuid)
        window.setTimeout(() => {
            setHighlightedUuid((current) => (current === parentCommentUuid ? null : current))
        }, 1600)
    }

    // Recursively fetches EVERY page and EVERY descendant level under `commentUuid`.
    // A node's own replyCount tells us whether it's worth descending into.
    const fetchAllDescendants = async (commentUuid: string): Promise<CrmComment[]> => {
        let page = 0
        let collected: CrmComment[] = []
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const res = await commentHooks.fetchCommentReplies(parentUuid, entityUuid, commentUuid, page)
            collected = [...collected, ...(res.content ?? [])]
            const totalPages = res.totalPages ?? 1
            if (res.last || page >= totalPages - 1) break
            page += 1
        }

        for (const item of collected) {
            if ((item.replyCount ?? 0) > 0 && !fetchedNestedFor.current.has(item.commentUuid)) {
                fetchedNestedFor.current.add(item.commentUuid)
                const nested = await fetchAllDescendants(item.commentUuid)
                collected = [...collected, ...nested]
            }
        }
        return collected
    }

    const loadLevel1Page = async (page: number) => {
        setIsLoadingReplies(true)
        try {
            const res = await commentHooks.fetchCommentReplies(parentUuid, entityUuid, comment.commentUuid, page)
            const level1Items = res.content ?? []
            setLevel1TotalPages(res.totalPages ?? 1)

            let nestedAll: CrmComment[] = []
            for (const item of level1Items) {
                if ((item.replyCount ?? 0) > 0 && !fetchedNestedFor.current.has(item.commentUuid)) {
                    fetchedNestedFor.current.add(item.commentUuid)
                    const nested = await fetchAllDescendants(item.commentUuid)
                    nestedAll = [...nestedAll, ...nested]
                }
            }

            const combined = [...level1Items, ...nestedAll]
            setAllReplies((prev) => sortByCreatedAt(dedupeByUuid(page === 0 ? combined : [...prev, ...combined])))
        } catch {
            toast.error('Failed to load replies')
        } finally {
            setIsLoadingReplies(false)
        }
    }

    const handleToggleExpand = () => {
        const next = !isExpanded
        setIsExpanded(next)
        if (next && allReplies.length === 0) {
            setLevel1Page(0)
            fetchedNestedFor.current = new Set()
            loadLevel1Page(0)
        }
    }

    const handleLoadMoreReplies = () => {
        const next = level1Page + 1
        setLevel1Page(next)
        loadLevel1Page(next)
    }

    const authorNameByUuid = useMemo(() => {
        const map = new Map<string, string>()
        map.set(comment.commentUuid, comment.createdByName || 'You')
        allReplies.forEach((r) => map.set(r.commentUuid, r.createdByName || 'You'))
        return map
    }, [comment, allReplies])

    const handleSubmitReply = () => {
        if (!replyText.trim() || isSubmitting || !replyTarget) return
        submitComment(
            { parentUuid, entityUuid, commentText: replyText, parentCommentUuid: replyTarget.commentUuid },
            {
                onSuccess: (created) => {
                    setReplyText('')
                    setReplyTarget(null)
                    if (created) {
                        setAllReplies((prev) => sortByCreatedAt(dedupeByUuid([...prev, created])))
                    }
                    setIsExpanded(true)
                },
            },
        )
    }

    const handleCopyLink = () => {
        navigator.clipboard
            .writeText(`${window.location.href.split('#')[0]}#comment-${comment.commentUuid}`)
            .then(() => toast.success('Link copied to clipboard'))
            .catch(() => toast.error('Failed to copy link'))
    }

    const handleStartEditReply = (reply: CrmComment) => {
        setEditingReplyUuid(reply.commentUuid)
        setEditingReplyText(reply.commentText)
    }

    const handleCancelEditReply = () => {
        setEditingReplyUuid(null)
        setEditingReplyText('')
    }

    const handleSaveEditReply = () => {
        if (!editingReplyUuid || !editingReplyText.trim()) return
        const target = allReplies.find((r) => r.commentUuid === editingReplyUuid)
        updateComment(
            {
                parentUuid,
                entityUuid,
                commentUuid: editingReplyUuid,
                commentText: editingReplyText,
                parentCommentUuid: target?.parentCommentUuid ?? undefined,
            },
            {
                onSuccess: () => {
                    setAllReplies((prev) =>
                        prev.map((r) =>
                            r.commentUuid === editingReplyUuid
                                ? { ...r, commentText: editingReplyText, isEdited: true }
                                : r,
                        ),
                    )
                    setEditingReplyUuid(null)
                    setEditingReplyText('')
                    onEdited?.()
                },
            },
        )
    }


    // Given a flat list of comments and a root uuid, returns the set of
    // commentUuids that are the root itself plus every nested descendant
    // (walks parentCommentUuid links, so it works for arbitrary depth).
    const collectSubtreeUuids = (rootUuid: string, list: CrmComment[]): Set<string> => {
        const ids = new Set<string>([rootUuid])
        let changed = true
        while (changed) {
            changed = false
            for (const c of list) {
                if (
                    c.parentCommentUuid &&
                    ids.has(c.parentCommentUuid) &&
                    !ids.has(c.commentUuid)
                ) {
                    ids.add(c.commentUuid)
                    changed = true
                }
            }
        }
        return ids
    }

    const handleConfirmDelete = () => {
        if (!deleteTarget) return
        deleteComment(
            { parentUuid, entityUuid, commentUuid: deleteTarget.commentUuid },
            {
                onSuccess: () => {
                    if (deleteTarget.commentUuid === comment.commentUuid) {
                        onDeleted?.()
                    } else {
                        const toRemove = collectSubtreeUuids(deleteTarget.commentUuid, allReplies)
                        toRemove.forEach((id) => fetchedNestedFor.current.delete(id))
                        setAllReplies((prev) => prev.filter((r) => !toRemove.has(r.commentUuid)))
                        onDeleted?.()
                    }
                    setDeleteTarget(null)
                },
            },
        )
    }

    const deleteDescendantCount = useMemo(() => {
        if (!deleteTarget) return 0
        if (deleteTarget.commentUuid === comment.commentUuid) {
            // Root delete: if expanded, allReplies is the accurate live count;
            // if not yet expanded, fall back to the server-reported replyCount.
            return isExpanded ? allReplies.length : (comment.replyCount ?? 0)
        }
        // Nested delete: allReplies is only reliable once expanded, which is
        // required to set a reply as deleteTarget in the first place.
        return collectSubtreeUuids(deleteTarget.commentUuid, allReplies).size - 1
    }, [deleteTarget, allReplies, comment.commentUuid, comment.replyCount, isExpanded])

    const deleteWarningMessage =
        deleteDescendantCount > 0
            ? `Are you sure you want to delete this comment? This will also permanently delete ${deleteDescendantCount} threaded ${deleteDescendantCount === 1 ? 'reply' : 'replies'
            }. This action cannot be undone.`
            : 'Are you sure you want to delete this comment? This action cannot be undone.'

    const isOwner = !!(currentUserId && comment.createdByUuid === currentUserId)

    return (
        <div id={`comment-${comment.commentUuid}`} className="relative min-w-0">
            <div
                className={`flex gap-3 group p-2 -m-2 rounded-lg min-w-0 transition-colors duration-500 ${highlightedUuid === comment.commentUuid ? 'bg-blue-500/10 ring-1 ring-blue-500/40' : ''
                    }`}
            >
                <div className="w-8 h-8 bg-primary-subtle rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {getInitials(comment.createdByName || (currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'U'))}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-neutral text-sm">
                                {comment.createdByName || 'You'}
                            </span>
                            <span className="text-neutral-medium text-xs">
                                {comment.createdAt ? getExactFormatDate(comment.createdAt) : 'just now'}
                            </span>
                            {comment.isEdited && (
                                <span className="text-[10px] bg-primary-subtle text-neutral-medium px-2 py-0.5 rounded-full">
                                    Edited
                                </span>
                            )}
                            <button onClick={handleCopyLink} className="p-1 rounded-md text-neutral-medium hover:text-blue-400 transition-colors" title="Copy link">
                                <Copy className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        {isOwner && !isEditingRoot && (
                            <Menu as="div" className="relative inline-block text-left flex-shrink-0">
                                <MenuButton className="p-1 rounded-md border border-blue-500/30 bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors">
                                    <MoreHorizontal className="w-4 h-4" />
                                </MenuButton>
                                <MenuItems anchor="bottom end" className="absolute right-0 mt-1 w-44 rounded-lg bg-primary-variant border border-primary-subtle shadow-lg z-20 overflow-hidden">
                                    <div className="p-1">
                                        <MenuItem>
                                            {({ focus }) => (
                                                <button
                                                    onClick={() => { setIsEditingRoot(true); setEditText(comment.commentText) }}
                                                    className={`${focus ? 'bg-primary-subtle text-neutral' : 'text-neutral-medium'} flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm`}
                                                >
                                                    <Edit2 className="w-4 h-4" /> Edit
                                                </button>
                                            )}
                                        </MenuItem>
                                        <MenuItem>
                                            {({ focus }) => (
                                                <button
                                                    onClick={() => setDeleteTarget(comment)}
                                                    className={`${focus ? 'bg-red-500/10 text-red-500' : 'text-red-500/80'} flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm`}
                                                >
                                                    <Trash2 className="w-4 h-4" /> Delete
                                                </button>
                                            )}
                                        </MenuItem>
                                    </div>
                                </MenuItems>
                            </Menu>
                        )}
                    </div>

                    {isEditingRoot ? (
                        <div className="mt-2 w-full border border-primary-subtle rounded-lg overflow-hidden bg-primary-variant min-w-0">
                            <TipTapEditor
                                initialContent={editText}
                                onChange={(c) => setEditText(c)}
                                placeholder="Edit comment..."
                                className={isUpdating ? 'opacity-50 pointer-events-none min-h-[60px]' : 'min-h-[60px]'}
                                mentionUsers={mentionUsers}
                                isComment
                            />
                            <div className="flex justify-end gap-2 p-2 bg-primary">
                                <button onClick={() => { setIsEditingRoot(false); setEditText(comment.commentText) }} disabled={isUpdating} className="px-3 py-1.5 text-xs font-medium text-neutral-medium hover:text-neutral hover:bg-primary-subtle rounded disabled:opacity-50">
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (!editText.trim() || editText === comment.commentText) { setIsEditingRoot(false); return }
                                        updateComment(
                                            { parentUuid, entityUuid, commentUuid: comment.commentUuid, commentText: editText, parentCommentUuid: comment.parentCommentUuid ?? undefined },
                                            { onSuccess: () => { setIsEditingRoot(false); onEdited?.() } },
                                        )
                                    }}
                                    disabled={isUpdating || !editText.trim()}
                                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                                >
                                    {isUpdating ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div
                            className="text-neutral-light text-sm leading-relaxed prose prose-sm prose-invert max-w-none mt-1 break-words overflow-x-auto [&_table]:w-full [&_table]:table-fixed [&_td]:break-words"
                            dangerouslySetInnerHTML={{ __html: comment.commentText }}
                        />
                    )}

                    {!isEditingRoot && (
                        <div className="flex items-center gap-4 mt-2">
                            <button
                                onClick={() => {
                                    setReplyTarget(comment)
                                    setReplyText('')
                                    setIsExpanded(true)
                                    if (allReplies.length === 0) {
                                        setLevel1Page(0)
                                        fetchedNestedFor.current = new Set()
                                        loadLevel1Page(0)
                                    }
                                }}
                                className="text-xs font-medium text-blue-400/80 hover:text-blue-400 flex items-center gap-1"
                            >
                                <MessageSquare className="w-3.5 h-3.5" /> Reply
                            </button>
                            {hasReplies && (
                                <button
                                    onClick={handleToggleExpand}
                                    className="text-xs font-semibold text-blue-400/90 hover:text-blue-400 flex items-center gap-1"
                                >
                                    {isExpanded ? <><ChevronDown className="w-3.5 h-3.5" /> Hide replies</> : <><ChevronRight className="w-3.5 h-3.5" /> View {comment.replyCount} {comment.replyCount === 1 ? 'reply' : 'replies'}</>}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Composer for replying to the ROOT comment — appears right below root, above the reply list */}
                    {replyTarget?.commentUuid === comment.commentUuid && (
                        <CrmReplyComposer
                            replyTarget={replyTarget}
                            replyText={replyText}
                            setReplyText={setReplyText}
                            onCancel={() => { setReplyTarget(null); setReplyText('') }}
                            onSubmit={handleSubmitReply}
                            isSubmitting={isSubmitting}
                            mentionUsers={mentionUsers}
                            currentUser={currentUser}
                        />
                    )}

                    {isExpanded && (
                        <div className="ml-4 pl-4 border-l border-primary-subtle mt-3 space-y-4 min-w-0">
                            {isLoadingReplies && allReplies.length === 0 ? (
                                <div className="flex items-center gap-2 py-2 text-xs text-neutral-medium animate-pulse">
                                    Loading replies...
                                </div>
                            ) : (
                                <>
                                    {allReplies.map((reply) => {
                                        const isReplyingToRoot =
                                            !reply.parentCommentUuid || reply.parentCommentUuid === comment.commentUuid
                                        const parentAuthorName = reply.parentCommentUuid
                                            ? authorNameByUuid.get(reply.parentCommentUuid)
                                            : undefined

                                        return (
                                            <React.Fragment key={reply.commentUuid}>
                                                <CrmReplyItem
                                                    reply={reply}
                                                    rootCommentUuid={comment.commentUuid}
                                                    currentUserId={currentUserId}
                                                    mentionUsers={mentionUsers}
                                                    parentAuthorName={parentAuthorName}
                                                    isReplyingToRoot={isReplyingToRoot}
                                                    isHighlighted={highlightedUuid === reply.commentUuid}
                                                    onReplyClick={(r) => {
                                                        setReplyTarget(r)
                                                        setReplyText('')
                                                    }}
                                                    onDeleteClick={(r) => setDeleteTarget(r)}
                                                    onJumpToParent={handleJumpToParent}
                                                    isEditing={editingReplyUuid === reply.commentUuid}
                                                    editText={editingReplyUuid === reply.commentUuid ? editingReplyText : reply.commentText}
                                                    onEditTextChange={setEditingReplyText}
                                                    onStartEdit={handleStartEditReply}
                                                    onCancelEdit={handleCancelEditReply}
                                                    onSaveEdit={handleSaveEditReply}
                                                    isUpdating={isUpdating}
                                                />

                                                {/* Composer for replying to THIS specific reply — appears right below it */}
                                                {replyTarget?.commentUuid === reply.commentUuid && (
                                                    <CrmReplyComposer
                                                        replyTarget={replyTarget}
                                                        replyText={replyText}
                                                        setReplyText={setReplyText}
                                                        onCancel={() => { setReplyTarget(null); setReplyText('') }}
                                                        onSubmit={handleSubmitReply}
                                                        isSubmitting={isSubmitting}
                                                        mentionUsers={mentionUsers}
                                                        currentUser={currentUser}
                                                    />
                                                )}
                                            </React.Fragment>
                                        )
                                    })}

                                    {level1Page < level1TotalPages - 1 && (
                                        <button onClick={handleLoadMoreReplies} disabled={isLoadingReplies} className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50">
                                            {isLoadingReplies ? 'Loading...' : 'Show more replies'}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <ConfirmDeleteModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                title="Delete Comment"
                message={deleteWarningMessage}
                isDeleting={isDeletePending}
                onConfirm={handleConfirmDelete}
            />
        </div>
    )
}

export default CrmCommentThread